import type { Channel, MessageDetail } from "@aiyo/shared";
import { ArrowRotateRight, Copy } from "@gravity-ui/icons";
import { Button, Table } from "@heroui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { DeliveryDrawer } from "../components/DeliveryDrawer";
import { DeliveryTable } from "../components/DeliveryTable";
import { MessageContent } from "../components/previews";
import { RetryDialog } from "../components/RetryDialog";
import {
  ChannelChip,
  Code,
  CodeBlock,
  ErrorBlock,
  KeyValue,
  LoadingBlock,
  MessageStatusChip,
  Page,
  PageHeader,
  Panel,
  Stat,
  StateChip,
} from "../components/ui";
import { shortId, useFormat } from "../lib/format";
import { useApp, useDeliveries, useMe, useMessage } from "../lib/queries";

const LIVE = new Set(["scheduled", "queued", "processing"]);

export function MessageDetailPage() {
  const { appId = "", messageId = "" } = useParams();
  const { t } = useTranslation();
  const fmt = useFormat();
  const message = useMessage(appId, messageId);
  const app = useApp(appId);
  const live = Boolean(message.data && LIVE.has(message.data.status));
  const deliveries = useDeliveries(appId, messageId, live);
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);
  const canEdit = me?.role !== "viewer";
  const status = message.data?.status;
  const refetchDeliveries = deliveries.refetch;
  // Delivery polling stops when the message finishes, so fetch once more whenever the status changes.
  useEffect(() => {
    if (status) void refetchDeliveries();
  }, [status, refetchDeliveries]);
  const [params, setParams] = useSearchParams();
  const openDelivery = params.get("delivery");
  const setOpenDelivery = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("delivery", id);
    else next.delete("delivery");
    setParams(next, { replace: true });
  };

  if (message.isPending) return <LoadingBlock />;
  if (message.isError) return <ErrorBlock error={message.error} onRetry={() => message.refetch()} />;
  const m = message.data;
  const rows = deliveries.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <span className="flex items-center gap-1.5">
            <Link to="/apps" className="hover:text-foreground">
              {t("nav.apps")}
            </Link>
            <span aria-hidden>/</span>
            <Link to={`/apps/${appId}`} className="hover:text-foreground">
              {app.data?.name ?? "…"}
            </Link>
            <span aria-hidden>/</span>
            <Link to={`/apps/${appId}?tab=messages`} className="hover:text-foreground">
              {t("appDetail.messages")}
            </Link>
          </span>
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{m.preview ?? <span className="font-mono">{shortId(m.id)}</span>}</span>
            <MessageStatusChip status={m.status} />
            {m.test_mode ? <StateChip ok="warn" label={t("keys.test")} /> : null}
          </span>
        }
        description={live ? t("messages.autoRefresh") : undefined}
        actions={
          canEdit ? (
            <>
              <Button variant="secondary" onPress={() => navigate(`/send?app=${appId}&from=${m.id}`)}>
                <Copy />
                {t("messages.duplicate")}
              </Button>
              {m.counts.failed + m.counts.skipped > 0 ? (
                <Button onPress={() => setRetrying(true)}>
                  <ArrowRotateRight />
                  {t("retry.open")}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t("messages.counts.total")} value={fmt.number(m.counts.total)} />
        <Stat label={t("messages.counts.pending")} value={fmt.number(m.counts.pending)} />
        <Stat label={t("messages.counts.sent")} value={fmt.number(m.counts.sent)} tone="accent" />
        <Stat label={t("messages.counts.delivered")} value={fmt.number(m.counts.delivered)} tone="success" />
        <Stat
          label={t("messages.counts.failed")}
          value={fmt.number(m.counts.failed)}
          tone={m.counts.failed ? "danger" : undefined}
        />
        <Stat label={t("messages.counts.skipped")} value={fmt.number(m.counts.skipped)} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
        <Panel title={t("appDetail.details")}>
          <KeyValue
            items={[
              { label: t("common.id"), value: <Code>{m.id}</Code> },
              { label: t("messages.priority"), value: t(`priority.${m.priority}`) },
              { label: t("messages.category"), value: m.category },
              { label: t("messages.audience"), value: audienceLabel(m.audience, t) },
              {
                label: t("messages.channels"),
                value: (
                  <div className="flex flex-wrap gap-1">
                    {m.channels.map((c) => (
                      <ChannelChip key={c} channel={c} />
                    ))}
                  </div>
                ),
              },
              { label: t("messages.createdAt"), value: fmt.dateTime(m.created_at) },
              { label: t("messages.completedAt"), value: fmt.dateTime(m.completed_at) },
            ]}
          />
          {m.metadata ? (
            <div className="flex flex-col gap-2">
              <div className="text-sm text-muted">{t("messages.metadata")}</div>
              <CodeBlock value={JSON.stringify(m.metadata, null, 2)} />
            </div>
          ) : null}
        </Panel>

        <Panel title={t("messages.perChannel")} className="self-start">
          <Table variant="secondary">
            <Table.ScrollContainer>
              <Table.Content aria-label={t("messages.perChannel")}>
                <Table.Header>
                  <Table.Column isRowHeader>{t("messages.channel")}</Table.Column>
                  <Table.Column className="text-end">{t("messages.counts.total")}</Table.Column>
                  <Table.Column className="text-end">{t("messages.counts.sent")}</Table.Column>
                  <Table.Column className="text-end">{t("messages.counts.delivered")}</Table.Column>
                  <Table.Column className="text-end">{t("messages.counts.failed")}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {Object.entries(m.per_channel).map(([channel, c]) => (
                    <Table.Row key={channel} id={channel}>
                      <Table.Cell>
                        <ChannelChip channel={channel as Channel} />
                      </Table.Cell>
                      <Table.Cell className="text-end tabular-nums">{c.total}</Table.Cell>
                      <Table.Cell className="text-end tabular-nums">{c.sent}</Table.Cell>
                      <Table.Cell className="text-end tabular-nums">{c.delivered}</Table.Cell>
                      <Table.Cell className={`text-end tabular-nums ${c.failed ? "text-danger" : ""}`}>
                        {c.failed}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        </Panel>
      </div>

      {m.content.length ? (
        <Panel title={t("messages.content")} description={t("messages.contentHint")}>
          <MessageContent items={m.content} appName={app.data?.name} />
        </Panel>
      ) : null}

      <section className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold">{t("messages.deliveries")}</h2>
        {deliveries.isError ? (
          <ErrorBlock error={deliveries.error} onRetry={() => deliveries.refetch()} />
        ) : (
          <DeliveryTable
            rows={rows}
            label={t("messages.deliveries")}
            onOpen={setOpenDelivery}
            showMessage={false}
            empty={t("common.loading")}
          />
        )}
        {deliveries.hasNextPage ? (
          <div className="flex justify-center">
            <Button
              variant="secondary"
              isPending={deliveries.isFetchingNextPage}
              onPress={() => deliveries.fetchNextPage()}
            >
              {t("common.loadMore")}
            </Button>
          </div>
        ) : null}
      </section>

      <DeliveryDrawer deliveryId={openDelivery} onClose={() => setOpenDelivery(null)} />
      <RetryDialog
        isOpen={retrying}
        onOpenChange={setRetrying}
        selection={{ filter: { app_id: appId, message_id: m.id } }}
      />
    </Page>
  );
}

function audienceLabel(
  a: MessageDetail["audience"],
  t: (key: never, opts?: Record<string, unknown>) => string,
) {
  if (a.all_customers) return t("messages.audienceAll" as never);
  const parts = [
    a.customers ? t("messages.audienceCustomers" as never, { count: a.customers }) : null,
    a.emails + a.phones + a.push_tokens
      ? t("messages.audienceAddresses" as never, { count: a.emails + a.phones + a.push_tokens })
      : null,
  ].filter(Boolean);
  return parts.join(" · ") || "—";
}
